import React, { useState } from 'react';
import { LogoMotion, LogoStyle } from '../../types/profile';

interface BrandLogoProps {
  src: string;
  alt: string;
  styleMode?: LogoStyle;
  motion?: LogoMotion;
  className?: string;
  maxWidth?: string;
  maxHeight?: string;
  background?: string;
  fallbackText?: string;
}

export const BrandLogo: React.FC<BrandLogoProps> = ({
  src,
  alt,
  styleMode = 'glow',
  motion = 'glow',
  className = '',
  maxWidth,
  maxHeight,
  background,
  fallbackText = 'Logo',
}) => {
  const [hasError, setHasError] = useState(false);

  // Styling modes - no forced white square!
  const styleClasses: Record<LogoStyle, string> = {
    minimal: 'bg-transparent p-1 border-0 shadow-none',
    glow: 'bg-slate-900/60 border border-sky-400/40 shadow-[0_0_25px_rgba(56,189,248,0.25)] p-2.5 rounded-2xl',
    glass: 'bg-slate-900/40 backdrop-blur-md border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.37)] p-2.5 rounded-2xl',
    solid: 'bg-slate-900 border border-slate-700/60 shadow-lg p-2.5 rounded-2xl',
  };

  // Optional motion effects (respecting prefers-reduced-motion)
  const motionClasses: Record<LogoMotion, string> = {
    none: '',
    reveal: 'animate-fade-in',
    float: 'animate-subtle-float',
    glow: 'animate-pulse-glow',
    subtlePulse: 'animate-subtle-pulse',
  };

  const customStyle: React.CSSProperties = {
    ...(background ? { backgroundColor: background } : {}),
    ...(maxWidth ? { maxWidth } : {}),
    ...(maxHeight ? { maxHeight } : {}),
  };

  const containerClasses = `
    relative inline-flex items-center justify-center
    transition-all duration-300 select-none
    ${styleClasses[styleMode] || styleClasses.glow}
    ${motionClasses[motion] || ''}
    ${className}
  `.trim();

  if (hasError) {
    return (
      <div
        className={`${containerClasses} w-20 h-20 bg-sky-950/80 border border-sky-500/30 text-sky-300 font-bold text-lg flex items-center justify-center`}
        style={customStyle}
      >
        {fallbackText}
      </div>
    );
  }

  return (
    <div className={containerClasses} style={customStyle}>
      <img
        src={src}
        alt={alt}
        onError={() => setHasError(true)}
        className="w-full h-full object-contain max-h-24 drop-shadow-md select-none pointer-events-none"
        loading="eager"
      />
    </div>
  );
};

